import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        destructive:
          'text-destructive-foreground [&>svg]:text-current *:data-[slot=alert-description]:text-destructive-foreground/80',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type AlertTitleProps = React.ComponentProps<'div'>;
type AlertDescriptionProps = React.ComponentProps<'div'>;

function AlertTitle({ className, ...props }: AlertTitleProps) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: AlertDescriptionProps) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-muted-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  );
}

type ValidAlertChild =
  | React.ReactElement<AlertTitleProps>
  | React.ReactElement<AlertDescriptionProps>
  | React.ReactElement<React.SVGProps<SVGSVGElement>>
  | false
  | null
  | undefined;

interface AlertProps extends React.ComponentProps<'div'>, VariantProps<typeof alertVariants> {
  children: ValidAlertChild | ValidAlertChild[];
}

function Alert({ className, variant, children, ...props }: AlertProps) {
  // Development-time validation
  if (import.meta.env['NODE_ENV'] === 'development') {
    React.Children.forEach(children, child => {
      if (!child) return; // Skip falsy children

      if (React.isValidElement(child)) {
        const childType = child.type;
        const isValidChild =
          childType === AlertTitle ||
          childType === AlertDescription ||
          (typeof childType === 'string' && childType === 'svg') ||
          (child.props && child.props['data-slot'] === 'alert-title') ||
          (child.props && child.props['data-slot'] === 'alert-description');

        if (!isValidChild) {
          console.error(
            '🚨 Alert component error: Alert can only contain AlertTitle, AlertDescription, or SVG icons as direct children.\n' +
              'Instead of: <Alert>Some text</Alert>\n' +
              'Use: <Alert><AlertDescription>Some text</AlertDescription></Alert>',
          );
        }
      } else if (typeof child === 'string' || typeof child === 'number') {
        console.error(
          '🚨 Alert component error: Alert cannot contain raw text as children.\n' +
            `Instead of: <Alert>${child}</Alert>\n` +
            `Use: <Alert><AlertDescription>${child}</AlertDescription></Alert>`,
        );
      }
    });
  }

  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {children}
    </div>
  );
}

export { Alert, AlertDescription, AlertTitle };
export type { AlertDescriptionProps, AlertProps, AlertTitleProps };
